import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  notificationsService,
  type NotificationUpcomingItem,
  type NotificationReminderRequest,
} from "@/services/notifications.service";

export interface ReminderPayload {
  title?: string;
  body?: string;
  deep_link?: string;
  data?: Record<string, unknown>;
}

interface UseTargetRemindersOptions {
  target: { type: string; id: string };
  payload: ReminderPayload;
  enabled?: boolean;
}

export interface UseTargetRemindersResult {
  reminders: NotificationUpcomingItem[];
  count: number;
  isLoading: boolean;
  createReminder: (fireAtMs: number, note?: string) => Promise<void>;
  deleteReminder: (notifId: string) => Promise<void>;
}

// Every badge reads one shared list of the user's upcoming reminders and picks
// out its own target. A request per target cost one call per visible row, and
// each of those walked the user's whole notification index server-side.
const ALL_UPCOMING_KEY = ["notifications-upcoming", "all"] as const;
const UPCOMING_PAGE_SIZE = 100;
const MAX_UPCOMING_PAGES = 10;

async function fetchAllUpcoming(): Promise<NotificationUpcomingItem[]> {
  const items: NotificationUpcomingItem[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_UPCOMING_PAGES; page++) {
    const response = await notificationsService.getUpcoming(UPCOMING_PAGE_SIZE, cursor);
    items.push(...(response.items ?? []));
    cursor = response.next_cursor ?? null;
    if (!cursor) break;
  }
  return items;
}

export function useTargetReminders({
  target,
  payload,
  enabled = true,
}: UseTargetRemindersOptions): UseTargetRemindersResult {
  const queryClient = useQueryClient();

  const selectTarget = useCallback(
    (items: NotificationUpcomingItem[]) =>
      items.filter(item => item.target_type === target.type && item.target_id === target.id),
    [target.type, target.id],
  );

  const { data, isLoading } = useQuery({
    queryKey: ALL_UPCOMING_KEY,
    queryFn: fetchAllUpcoming,
    select: selectTarget,
    enabled: enabled && Boolean(target.id),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const reminders = useMemo(() => data ?? [], [data]);
  const count = reminders.length;

  const createReminder = useCallback(
    async (fireAtMs: number, note?: string) => {
      if (fireAtMs <= Date.now() + 5000) {
        toast.error("Reminder time must be in the future.");
        return;
      }

      // Exclude the default body — only include body when the user writes a custom note.
      const { body: _defaultBody, ...payloadWithoutBody } = payload;
      const request: NotificationReminderRequest = {
        trigger: { kind: "absolute", fire_at_ms: fireAtMs },
        target: { type: target.type, id: target.id },
        payload: {
          ...payloadWithoutBody,
          ...(note ? { body: note } : {}),
        },
      };

      try {
        await notificationsService.createReminder(request);
        await queryClient.invalidateQueries({
          queryKey: ["notifications-upcoming"],
        });
      } catch (error) {
        console.error("Failed to create reminder:", error);
        toast.error("Failed to set reminder.");
      }
    },
    [target.type, target.id, payload, queryClient],
  );

  const deleteReminder = useCallback(
    async (notifId: string) => {
      try {
        await notificationsService.cancelReminder({ notification_id: notifId });
        await queryClient.invalidateQueries({
          queryKey: ["notifications-upcoming"],
        });
      } catch (error) {
        console.error("Failed to cancel reminder:", error);
        toast.error("Failed to cancel reminder.");
      }
    },
    [queryClient],
  );

  return { reminders, count, isLoading, createReminder, deleteReminder };
}
